/**
 * Billing Plans — list all tenants and inline-edit their billing plans (Phase 8.5).
 *
 * Reads: GET /admin/tenants (includes billing_plan join).
 * Writes: PUT /admin/tenants/{id}/billing-plan.
 *
 * Each tenant row expands into a plan editor when clicked.
 */

import { Fragment, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { billingService } from "@re-platform/api-client";
import type {
  BillingPlanBody,
  BillingPlanCatalogEntry,
  PlanType,
} from "@re-platform/api-client";
import { Card } from "@re-platform/ui";

import { api } from "../../lib/api";
import {
  bodyRow,
  tableShell,
  TableScroll,
  Td,
  Th,
  theadRow,
} from "../../lib/tables";

interface TenantRow {
  id: string;
  slug: string;
  display_name: string;
  status: string;
  billing_plan: {
    plan_type: string;
    base_fee: string;
    agent_fee: string;
    markup_pct: string;
    flat_rate: string;
    discount_pct: string;
    monthly_minimum?: string;
    catalog_plan_id?: string | null;
    max_agents?: number | null;
    max_admins?: number | null;
    stripe_customer_id: string | null;
    active: boolean;
  } | null;
}

const PLAN_TYPES: PlanType[] = [
  "PER_AGENT",
  "PER_USAGE",
  "FLAT_RATE",
  "WHOLESALE",
];

function seatLabel(n: number | null | undefined): string {
  return n === null || n === undefined ? "unlimited" : String(n);
}

function PlanEditor({
  tenantId,
  initial,
  catalog,
  onDone,
}: {
  tenantId: string;
  initial: TenantRow["billing_plan"];
  catalog: BillingPlanCatalogEntry[];
  onDone: (msg: string) => void;
}) {
  const [form, setForm] = useState<BillingPlanBody>({
    plan_type: (initial?.plan_type as PlanType) ?? "FLAT_RATE",
    base_fee: initial?.base_fee ?? "0.00",
    agent_fee: initial?.agent_fee ?? "0.00",
    markup_pct: initial?.markup_pct ?? "0.00",
    flat_rate: initial?.flat_rate ?? "0.00",
    discount_pct: initial?.discount_pct ?? "0.00",
    monthly_minimum: initial?.monthly_minimum ?? "0.00",
    catalog_plan_id: initial?.catalog_plan_id ?? null,
    max_agents: initial?.max_agents ?? null,
    max_admins: initial?.max_admins ?? null,
    stripe_customer_id: initial?.stripe_customer_id ?? null,
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  // Picking a named catalog plan pre-fills pricing + seat limits + minimum; the
  // admin can then tweak any field before saving (pricing is body-authoritative).
  function applyCatalog(planId: string) {
    const plan = catalog.find((c) => c.id === planId);
    if (!plan) {
      setForm((f) => ({ ...f, catalog_plan_id: null }));
      return;
    }
    setForm((f) => ({
      ...f,
      catalog_plan_id: plan.id,
      plan_type: plan.plan_type,
      base_fee: plan.base_fee,
      agent_fee: plan.agent_fee,
      markup_pct: plan.markup_pct,
      flat_rate: plan.flat_rate,
      discount_pct: plan.discount_pct,
      monthly_minimum: plan.monthly_minimum,
      max_agents: plan.max_agents,
      max_admins: plan.max_admins,
    }));
  }

  async function save() {
    setSaving(true);
    try {
      await billingService.putBillingPlan(api, tenantId, form);
      onDone("Plan saved.");
    } catch (err) {
      onDone(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function seatField(label: string, key: "max_agents" | "max_admins") {
    return (
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>{label}</span>
        <input
          type="number"
          min={0}
          placeholder="unlimited"
          value={form[key] ?? ""}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              [key]: e.target.value === "" ? null : Number(e.target.value),
            }))
          }
          style={{
            padding: "4px 8px",
            border: "1px solid var(--rp-color-border)",
            borderRadius: "var(--rp-radius-sm)",
            fontSize: 13,
            width: 160,
          }}
        />
      </label>
    );
  }

  function field(label: string, key: keyof BillingPlanBody, type = "text") {
    return (
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>{label}</span>
        <input
          type={type}
          value={String(form[key] ?? "")}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          style={{
            padding: "4px 8px",
            border: "1px solid var(--rp-color-border)",
            borderRadius: "var(--rp-radius-sm)",
            fontSize: 13,
            width: 160,
          }}
        />
      </label>
    );
  }

  return (
    <div
      style={{
        background: "var(--rp-color-surface-raised)",
        border: "1px solid var(--rp-color-border)",
        borderRadius: "var(--rp-radius-md, 6px)",
        padding: 16,
        marginTop: 8,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {catalog.length > 0 && (
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 13,
          }}
        >
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            Start from plan
          </span>
          <select
            value={form.catalog_plan_id ?? ""}
            onChange={(e) => applyCatalog(e.target.value)}
            style={{
              padding: "4px 8px",
              border: "1px solid var(--rp-color-border)",
              borderRadius: "var(--rp-radius-sm)",
              fontSize: 13,
              width: 260,
            }}
          >
            <option value="">Custom (no catalog plan)</option>
            {catalog.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.plan_type} · {seatLabel(c.max_agents)} agents /{" "}
                {seatLabel(c.max_admins)} admins
              </option>
            ))}
          </select>
        </label>
      )}

      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>Plan type</span>
        <select
          value={form.plan_type}
          onChange={(e) =>
            setForm((f) => ({ ...f, plan_type: e.target.value as PlanType }))
          }
          style={{
            padding: "4px 8px",
            border: "1px solid var(--rp-color-border)",
            borderRadius: "var(--rp-radius-sm)",
            fontSize: 13,
            width: 180,
          }}
        >
          {PLAN_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {field("Base fee ($)", "base_fee")}
        {form.plan_type === "PER_AGENT" && field("Agent fee ($)", "agent_fee")}
        {form.plan_type === "PER_USAGE" &&
          field("Monthly minimum ($)", "monthly_minimum")}
        {form.plan_type === "FLAT_RATE" && field("Flat rate ($)", "flat_rate")}
        {form.plan_type === "WHOLESALE" && field("Discount %", "discount_pct")}
        {field("Stripe customer ID", "stripe_customer_id")}
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {seatField("Max agents", "max_agents")}
        {seatField("Max admins", "max_admins")}
      </div>
      <p
        style={{ fontSize: 12, color: "var(--rp-color-text-muted)", margin: 0 }}
      >
        Leave a seat limit blank to inherit the catalog plan (or unlimited if
        custom). Blocking kicks in when a brokerage tries to invite past it.
      </p>

      <label
        style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}
      >
        <input
          type="checkbox"
          checked={!!form.active}
          onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
        />
        Active
      </label>

      <div>
        <button
          type="button"
          className="rp-btn rp-btn--primary rp-btn--sm"
          disabled={saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save plan"}
        </button>
      </div>
    </div>
  );
}

export function BillingPlans() {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const q = useQuery<TenantRow[]>({
    queryKey: ["tenants"],
    queryFn: () => api.request("/admin/tenants"),
    staleTime: 30_000,
  });

  const catalogQuery = useQuery<BillingPlanCatalogEntry[]>({
    queryKey: ["plan-catalog"],
    queryFn: () => billingService.getPlanCatalog(api),
    staleTime: 300_000,
  });

  function onDone(msg: string) {
    setToast(msg);
    setExpanded(null);
    void queryClient.invalidateQueries({ queryKey: ["tenants"] });
    setTimeout(() => setToast(null), 3000);
  }

  if (q.isLoading)
    return <p style={{ color: "var(--rp-color-text-muted)" }}>Loading…</p>;
  if (q.isError)
    return <p style={{ color: "var(--rp-color-danger)" }}>{String(q.error)}</p>;

  const tenants = q.data ?? [];

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Billing plans</h1>
      {toast && (
        <div
          style={{
            background: "var(--rp-color-success-faint)",
            border: "1px solid var(--rp-color-success, green)",
            borderRadius: "var(--rp-radius-sm)",
            padding: "8px 12px",
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {toast}
        </div>
      )}

      <p
        style={{
          color: "var(--rp-color-text-muted)",
          fontSize: 13,
          marginBottom: 12,
        }}
      >
        Click a tenant to assign or edit its billing plan.
      </p>

      <Card padding="none" style={{ overflow: "hidden" }}>
        <TableScroll>
          <table style={tableShell}>
            <thead>
              <tr style={theadRow}>
                {["Tenant", "Status", "Plan type", "Active"].map((h) => (
                  <Th key={h}>{h}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.length === 0 && (
                <tr style={bodyRow}>
                  <Td muted>No tenants found.</Td>
                </tr>
              )}
              {tenants.map((t) => (
                // Fragment must carry the key: the shorthand <> cannot, so the key
                // sat on the inner <tr> — which is not the mapped child, and React
                // warned about every row on this screen.
                <Fragment key={t.id}>
                  <tr
                    onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                    style={{
                      cursor: "pointer",
                      borderBottom:
                        expanded === t.id
                          ? "none"
                          : "1px solid var(--rp-color-border)",
                    }}
                    onMouseEnter={(e) =>
                      ((
                        e.currentTarget as HTMLTableRowElement
                      ).style.background = "var(--rp-color-surface-hover)")
                    }
                    onMouseLeave={(e) =>
                      ((
                        e.currentTarget as HTMLTableRowElement
                      ).style.background = "")
                    }
                  >
                    <Td>
                      <strong>{t.display_name}</strong>{" "}
                      <span
                        style={{
                          fontSize: 12,
                          color: "var(--rp-color-text-muted)",
                        }}
                      >
                        {t.slug}
                      </span>
                    </Td>
                    <Td>
                      <span
                        style={{
                          fontSize: 11,
                          padding: "2px 8px",
                          borderRadius: 99,
                          background:
                            t.status === "ACTIVE"
                              ? "var(--rp-color-success-faint)"
                              : "var(--rp-color-surface-raised)",
                          color:
                            t.status === "ACTIVE"
                              ? "var(--rp-color-success, green)"
                              : "var(--rp-color-text-muted)",
                        }}
                      >
                        {t.status}
                      </span>
                    </Td>
                    <Td>
                      {t.billing_plan?.plan_type ?? (
                        <span
                          style={{
                            color: "var(--rp-color-text-muted)",
                            fontSize: 12,
                          }}
                        >
                          none — click to assign
                        </span>
                      )}
                    </Td>
                    <Td>
                      {t.billing_plan
                        ? t.billing_plan.active
                          ? "✓"
                          : "✗"
                        : "—"}
                    </Td>
                  </tr>
                  {expanded === t.id && (
                    <tr style={bodyRow}>
                      <td colSpan={4} style={{ padding: "0 12px 12px" }}>
                        <PlanEditor
                          tenantId={t.id}
                          initial={t.billing_plan}
                          catalog={catalogQuery.data ?? []}
                          onDone={onDone}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </Card>
    </>
  );
}
